import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, Download, RefreshCw } from 'lucide-react';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { SongTable } from '../components/songs/SongTable';
import { SongDetails } from '../components/songs/SongDetails';
import { useToast } from '../components/common/Toast';
import { useAppData } from '../context/DataContext';
import { Song, SongStatus } from '../types';

export const LiveSongs: React.FC = () => {
  const { songs, artists, updateSong, deleteSong } = useAppData();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SongStatus | 'all'>('all');
  const [artistFilter, setArtistFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'title' | 'streams' | 'revenue' | 'releaseDate'>('releaseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const { addToast } = useToast();

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'live', label: 'Live' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'taken_down', label: 'Taken Down' }
  ];

  const artistOptions = [
    { value: 'all', label: 'All Artists' },
    ...artists.map(artist => ({
      value: artist.id,
      label: artist.name
    }))
  ];

  const sortOptions = [
    { value: 'releaseDate', label: 'Release Date' },
    { value: 'title', label: 'Title' },
    { value: 'streams', label: 'Streams' },
    { value: 'revenue', label: 'Revenue' }
  ];

  const filteredAndSortedSongs = useMemo(() => {
    let filtered = songs.filter(song => {
      const matchesSearch = song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           song.artist.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || song.status === statusFilter;
      const matchesArtist = artistFilter === 'all' || song.artistId === artistFilter;
      
      return matchesSearch && matchesStatus && matchesArtist;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'streams':
          aValue = a.streams;
          bValue = b.streams;
          break;
        case 'revenue':
          aValue = a.revenue;
          bValue = b.revenue;
          break;
        case 'releaseDate':
          aValue = new Date(a.releaseDate).getTime();
          bValue = new Date(b.releaseDate).getTime();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [songs, searchQuery, statusFilter, artistFilter, sortBy, sortOrder]);

  const handleEdit = (song: Song) => {
    addToast({
      type: 'info',
      title: 'Edit Song',
      message: `Opening editor for "${song.title}"`
    });
  };

  const handleDelete = (songId: string) => {
    const song = songs.find(s => s.id === songId);
    if (song) {
      deleteSong(songId);
      addToast({
        type: 'success',
        title: 'Song Deleted',
        message: `"${song.title}" has been removed from distribution.`
      });
    }
  };

  const handleViewDetails = (song: Song) => {
    setSelectedSong(song);
    setShowDetails(true);
  };

  const handleRefresh = () => {
    addToast({
      type: 'info',
      title: 'Refreshing',
      message: 'Updating song statuses from distribution platforms...'
    });
    
    setTimeout(() => {
      addToast({
        type: 'success',
        title: 'Updated',
        message: 'Song statuses have been refreshed.'
      });
    }, 2000);
  };

  const handleExportData = () => {
    addToast({
      type: 'success',
      title: 'Export Started',
      message: 'Your song data is being prepared for download.'
    });
  };

  const getStatusCounts = () => {
    return {
      live: songs.filter(s => s.status === 'live').length,
      pending: songs.filter(s => s.status === 'pending').length,
      failed: songs.filter(s => s.status === 'failed').length,
      taken_down: songs.filter(s => s.status === 'taken_down').length
    };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center p-4 bg-green-500/10 border-green-500/20">
          <div className="text-2xl font-bold text-green-400 mb-1">
            {statusCounts.live}
          </div>
          <div className="text-sm text-gray-400">Live Songs</div>
        </Card>
        <Card className="text-center p-4 bg-yellow-500/10 border-yellow-500/20">
          <div className="text-2xl font-bold text-yellow-400 mb-1">
            {statusCounts.pending}
          </div>
          <div className="text-sm text-gray-400">Pending</div>
        </Card>
        <Card className="text-center p-4 bg-red-500/10 border-red-500/20">
          <div className="text-2xl font-bold text-red-400 mb-1">
            {statusCounts.failed}
          </div>
          <div className="text-sm text-gray-400">Failed</div>
        </Card>
        <Card className="text-center p-4 bg-gray-500/10 border-gray-500/20">
          <div className="text-2xl font-bold text-gray-400 mb-1">
            {statusCounts.taken_down}
          </div>
          <div className="text-sm text-gray-400">Taken Down</div>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex-1 min-w-64">
              <Input
                placeholder="Search songs or artists..."
                value={searchQuery}
                onChange={setSearchQuery}
                icon={Search}
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as SongStatus | 'all')}
              options={statusOptions}
              className="min-w-48"
            />
            <Select
              value={artistFilter}
              onChange={setArtistFilter}
              options={artistOptions}
              className="min-w-48"
            />
          </div>
          
          <div className="flex gap-2">
            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value as any)}
              options={sortOptions}
              className="min-w-40"
            />
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
              icon={RefreshCw}
            >
            </Button>
            <Button
              variant="outline"
              onClick={handleExportData}
              icon={Download}
            >
            </Button>
          </div>
        </div>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>
          Showing {filteredAndSortedSongs.length} of {songs.length} songs
        </span>
        {(searchQuery || statusFilter !== 'all' || artistFilter !== 'all') && (
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setArtistFilter('all');
            }}
            className="text-gold-400 hover:text-gold-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Songs Table */}
      <SongTable
        songs={filteredAndSortedSongs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onViewDetails={handleViewDetails}
      />

      {/* Empty State */}
      {filteredAndSortedSongs.length === 0 && (
        <Card className="text-center p-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No songs found</h3>
          <p className="text-gray-400 mb-6">
            {searchQuery || statusFilter !== 'all' || artistFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Start by distributing your first song.'}
          </p>
          <Button icon={Plus}>
            Distribute New Song
          </Button>
        </Card>
      )}

      {/* Song Details Modal */}
      <SongDetails
        song={selectedSong}
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedSong(null);
        }}
      />
    </div>
  );
};